/**
 * Product Pages JavaScript - Crown Theme
 * Handles product page interactions and functionality
 */

class ProductPage {
  constructor() {
    this.init();
  }

  init() {
    this.setupVariantSelection();
    this.setupDescriptionToggle();
    this.setupShippingLookup();
    this.setupImageLazyLoading();
    this.setupShareFunctionality();
  }

  /**
   * Setup product variant selection
   */
  setupVariantSelection() {
    const variantOptions = document.querySelectorAll('.variant-option');
    
    variantOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();
        
        // Remove selected class from siblings
        const group = option.closest('.variant-options');
        group.querySelectorAll('.variant-option').forEach(opt => {
          opt.classList.remove('variant-option--selected');
        });
        
        // Add selected class to clicked option
        option.classList.add('variant-option--selected');
        
        // Update variant selection
        this.updateVariantSelection(option);
      });
    });
  }

  /**
   * Update variant selection and product details
   */
  updateVariantSelection(selectedOption) {
    const value = selectedOption.dataset.value;
    const variantGroup = selectedOption.closest('.variant-group');
    const variantName = variantGroup.querySelector('.variant-name').textContent;
    const variantSelected = variantGroup.querySelector('.variant-selected');
    
    if (variantSelected) {
      variantSelected.textContent = value;
    }
    
    // Here you would typically update the product variant
    // This would involve updating the product form, price, availability, etc.
    console.log(`Selected ${variantName}: ${value}`);
  }

  /**
   * Setup product description toggle
   */
  setupDescriptionToggle() {
    const toggleButton = document.querySelector('.product-description__toggle');
    const descriptionContent = document.querySelector('.product-description__content');
    
    if (toggleButton && descriptionContent) {
      toggleButton.addEventListener('click', () => {
        const isExpanded = descriptionContent.style.maxHeight && descriptionContent.style.maxHeight !== 'none';
        
        if (isExpanded) {
          descriptionContent.style.maxHeight = 'none';
          toggleButton.textContent = '+ Descrição';
        } else {
          descriptionContent.style.maxHeight = descriptionContent.scrollHeight + 'px';
          toggleButton.textContent = '- Descrição';
        }
      });
    }
  }

  /**
   * Setup shipping CEP lookup
   */
  setupShippingLookup() {
    const cepInput = document.getElementById('cep-input');
    const shippingForm = document.getElementById('shipping-lookup-form');
    
    if (cepInput) {
      // Format CEP input
      cepInput.addEventListener('input', (e) => {
        let value = e.target.value.replace(/\D/g, '');
        if (value.length > 5) {
          value = value.substring(0, 5) + '-' + value.substring(5, 8);
        }
        e.target.value = value;
      });
    }
    
    if (shippingForm) {
      shippingForm.addEventListener('submit', (e) => {
        e.preventDefault();
        const cep = cepInput.value.replace(/\D/g, '');
        
        if (cep.length === 8) {
          this.lookupShipping(cep);
        } else {
          this.showShippingError('Por favor, digite um CEP válido');
        }
      });
    }
  }

  /**
   * Lookup shipping options for CEP
   */
  async lookupShipping(cep) {
    try {
      // Show loading state
      this.showShippingLoading();
      
      // Here you would make an API call to get shipping options
      // For now, we'll simulate a response
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simulate shipping options
      const shippingOptions = [
        { name: 'Entrega Padrão', price: 'R$ 15,90', days: '5-7 dias úteis' },
        { name: 'Entrega Expressa', price: 'R$ 25,90', days: '2-3 dias úteis' },
        { name: 'Retirada na Loja', price: 'Grátis', days: 'Imediato' }
      ];
      
      this.showShippingOptions(shippingOptions);
    } catch (error) {
      this.showShippingError('Erro ao consultar frete. Tente novamente.');
    }
  }

  /**
   * Show shipping loading state
   */
  showShippingLoading() {
    const shippingForm = document.querySelector('.shipping-lookup__form');
    if (shippingForm) {
      const button = shippingForm.querySelector('.shipping-lookup__button');
      if (button) {
        button.textContent = 'Consultando...';
        button.disabled = true;
      }
    }
  }

  /**
   * Show shipping options
   */
  showShippingOptions(options) {
    // Remove existing options
    const existingOptions = document.querySelector('.shipping-options');
    if (existingOptions) {
      existingOptions.remove();
    }
    
    // Create options container
    const optionsContainer = document.createElement('div');
    optionsContainer.className = 'shipping-options';
    optionsContainer.innerHTML = `
      <h3>Opções de entrega:</h3>
      ${options.map(option => `
        <div class="shipping-option">
          <div class="shipping-option__info">
            <span class="shipping-option__name">${option.name}</span>
            <span class="shipping-option__days">${option.days}</span>
          </div>
          <span class="shipping-option__price">${option.price}</span>
        </div>
      `).join('')}
    `;
    
    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .shipping-options {
        margin-top: 1rem;
        padding: 1rem;
        background: #f8f8f8;
        border-radius: 8px;
      }
      .shipping-options h3 {
        margin: 0 0 1rem 0;
        font-size: 1rem;
        color: var(--color-foreground);
      }
      .shipping-option {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 0;
        border-bottom: 1px solid #e0e0e0;
      }
      .shipping-option:last-child {
        border-bottom: none;
      }
      .shipping-option__name {
        font-weight: 500;
        color: var(--color-foreground);
      }
      .shipping-option__days {
        font-size: 0.875rem;
        color: var(--color-foreground-75);
      }
      .shipping-option__price {
        font-weight: 500;
        color: var(--color-foreground);
      }
    `;
    
    document.head.appendChild(style);
    
    // Insert after form
    const shippingForm = document.querySelector('.shipping-lookup__form');
    if (shippingForm) {
      shippingForm.parentNode.insertBefore(optionsContainer, shippingForm.nextSibling);
    }
    
    // Reset form
    const button = shippingForm.querySelector('.shipping-lookup__button');
    if (button) {
      button.textContent = 'Consultar';
      button.disabled = false;
    }
  }

  /**
   * Show shipping error
   */
  showShippingError(message) {
    // Remove existing options
    const existingOptions = document.querySelector('.shipping-options');
    if (existingOptions) {
      existingOptions.remove();
    }
    
    // Show error message
    alert(message);
    
    // Reset form
    const shippingForm = document.querySelector('.shipping-lookup__form');
    if (shippingForm) {
      const button = shippingForm.querySelector('.shipping-lookup__button');
      if (button) {
        button.textContent = 'Consultar';
        button.disabled = false;
      }
    }
  }

  /**
   * Setup image lazy loading
   */
  setupImageLazyLoading() {
    const images = document.querySelectorAll('img[loading="lazy"]');
    
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.classList.add('loaded');
            observer.unobserve(img);
          }
        });
      });
      
      images.forEach(img => imageObserver.observe(img));
    } else {
      // Fallback for browsers without IntersectionObserver
      images.forEach(img => img.classList.add('loaded'));
    }
  }

  /**
   * Setup share functionality
   */
  setupShareFunctionality() {
    const shareButton = document.querySelector('.btn--share');
    
    if (shareButton) {
      shareButton.addEventListener('click', () => {
        if (navigator.share) {
          navigator.share({
            title: document.title,
            url: window.location.href
          }).catch(err => {
            console.log('Error sharing:', err);
            this.fallbackShare();
          });
        } else {
          this.fallbackShare();
        }
      });
    }
  }

  /**
   * Fallback share functionality
   */
  fallbackShare() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        this.showShareSuccess();
      }).catch(() => {
        this.showShareError();
      });
    } else {
      this.showShareError();
    }
  }

  /**
   * Show share success message
   */
  showShareSuccess() {
    const button = document.querySelector('.btn--share');
    if (button) {
      const originalText = button.textContent;
      button.textContent = 'Link copiado!';
      button.style.background = '#4CAF50';
      button.style.color = 'white';
      
      setTimeout(() => {
        button.textContent = originalText;
        button.style.background = '';
        button.style.color = '';
      }, 2000);
    }
  }

  /**
   * Show share error message
   */
  showShareError() {
    alert('Não foi possível compartilhar. Tente copiar o link manualmente.');
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new ProductPage();
});

// Export for potential external use
window.ProductPage = ProductPage;
